<template>
  <div class="finances-payouts-items-table">
    <b-table :fields="fields" :items="items" class="mb-0">
      <template #cell(contractor)="{ item, index }">
        <multiselect
          class="multiselect-small-line-height"
          :class="{ 'is-not-valid': item.isNotValid, 'user-select': true }"
          v-model="item.contractor"
          @input="payoutItemChanged(index, 'contractor', $event)"
          :placeholder="currentChoice"
          :options="getContractorsSelectOptions"
          track-by="id"
          label="label"
          return="id"
          :showLabels="false"
          :disabled="!!currentContractor"
          allow-empty
        ></multiselect>
      </template>
      <template #cell(project)="{ item, index }">
        <multiselect
          class="user-select multiselect-small-line-height"
          v-model="item.project"
          @input="payoutItemChanged(index, 'project', $event)"
          :options="getProjectsWithVirtual"
          track-by="id"
          label="label"
          return="id"
          :showLabels="false"
          :disabled="!item.contractor"
          allow-empty
        ></multiselect>
      </template>
      <template #cell(hour_cost)="{ item, index }">
        <span
          class="cursor-pointer hour-cost"
          v-b-tooltip.hover="{ variant: 'base' }"
          :title="item.compensation_currency_code ? item.compensation_currency_code : ''"
        >
          <b-form-input
            type="number"
            no-wheel
            v-model="item.hour_cost"
            @input="payoutItemChanged(index, 'hour_cost', $event)"
          />
          <span
            v-if="item.payment_type && item.payment_type === 'hourly'"
            class="payment-type-label"
            v-b-tooltip.hover="{ variant: 'base' }"
            title="Per hour">/h </span>
          <span
            v-else
            class="payment-type-label"
            v-b-tooltip.hover="{ variant: 'base' }"
            title="Per month">/m</span>
        </span>
      </template>
      <template #cell(hours)="{ item, index }">
        <span
          class="cursor-pointer"
          v-b-tooltip.hover="{ variant: 'base' }"
          :title="item.amount ? 'Calculated cost: ' + item.amount : ''"
        >
          <b-form-input
            type="number"
            step="0.01"
            no-wheel
            v-model="item.hours"
            @input="payoutItemChanged(index, 'hours', $event)"
          />
        </span>
      </template>
      <template #cell(logged_hours)="{ item, index }">
        <div style="margin-top: 3px;">{{ formatNumber(item.logged_hours) }}</div>
        <span class="cursor-pointer hours-info">
          <div
            v-if="item.hoursCalculating"
            class="hours-loader spinner-border text-secondary"
            role="status">
            <span class="sr-only">Loading...</span>
          </div>
        </span>
      </template>
      <template #cell(calculateHours)="{ item, index }">
        <b-form-checkbox
          type="checkbox"
          size="lg"
          v-model="item.calculateHours"
          @input="payoutItemChanged(index, 'calculateHours', $event)"
          :value="true"
          v-b-tooltip.hover="{ variant: 'base' }"
          title="Auto calculate hours from worklogs"
        />
      </template>
      <template #cell(date_from)="{ item, index }">
        <b-form-group
          style="position: relative;">
          <b-form-input
            style="position: absolute;"
            :value="convertDataToString(item.date_from)"
            required
          />
          <Datepicker
            :bootstrap-styling="true"
            :value="_toIsoStringTime(item.date_from)"
            @input="item.date_from = _toLocalTime($event); payoutItemChanged(index, 'date_from', $event)"
            format="yyyy-MM-dd"
            v-b-tooltip.hover="{ variant: 'base' }"
            :title="item.created_periods ? 'Auto fill from the end of last period: ' + item.created_periods.date_from + ' - ' + item.created_periods.date_to : ''"
          />
        </b-form-group>
      </template>
      <template #cell(date_to)="{ item, index }">
        <b-form-group
          style="position: relative;">
          <b-form-input
            style="position: absolute;"
            :value="convertDataToString(item.date_to)"
            required
          />
          <Datepicker
            :bootstrap-styling="true"
            :value="_toIsoStringTime(item.date_to)"
            @input="item.date_to = _toLocalTime($event); payoutItemChanged(index, 'date_to', $event)"
            format="yyyy-MM-dd"
          />
        </b-form-group>
      </template>
      <template #cell(actions)="data" v-if="items.length > 1">
       <b-button variant="danger" size="sm" @click.prevent="removeItem(data)">
         <font-awesome-icon icon="trash" />
       </b-button>
      </template>
    </b-table>
    <div>
      <label>*</label> - dates are not involved in calculations, they are used only for manual data reconciliation
    </div>
    <b-button @click.prevent="addItem" variant="info">
      Add item
    </b-button>
  </div>
</template>

<script>
import FormItemsMixin from '../../../mixins/FormItemsMixin'
import { mapGetters } from 'vuex'
import { cloneDeep } from 'lodash'

export default {
  mixins: [FormItemsMixin],
  props: {
    currentContractor: {
      type: [Object, null],
      default: null
    },
    currentPartner: {
      type: [Object, null],
      default: null
    },
    currentChoice: {
      type: String,
      default: ''
    }
  },
  watch: {
    currentContractor: {
      deep: true,
      handler (v) {
        if (v && this.items.length === 0) {
          this.items[0] = { ...this.defaultItem }
          this.items[0].contractor = v
        }
        this.currentContractor = v
      }
    },
    currentPartner: {
      deep: true,
      handler (v) {
        this.currentPartner = v
      }
    }
  },
  data () {
    return {
      fields: [
        { key: 'contractor', label: 'Contractor', class: 'select-column' },
        { key: 'project', label: 'Project', class: 'select-column' },
        { key: 'hour_cost', label: 'Rate', class: 'cost-column' },
        { key: 'hours', label: 'Approved hours', class: 'hours-column' },
        { key: 'logged_hours', label: 'Logged hours', class: 'logged-hours-column' },
        { key: 'calculateHours', label: 'Auto', class: 'calculate-hours-column' },
        { key: 'date_from', label: 'Date from*', class: 'date-from-column date-column' },
        { key: 'date_to', label: 'Date to*', class: 'date-to-column date-column' },
        { key: 'actions', label: '', class: 'actions-column' }
      ],
      items: []
    }
  },
  computed: {
    ...mapGetters({
      getProjectsWithVirtual: 'projects/getProjectsWithVirtual'
    }),
    defaultItem () {
      return {
        'contractor': this.currentContractor ?? null,
        'project': null,
        'hours': 0,
        'logged_hours': 0,
        'hour_cost': 0,
        'date_from': null,
        'date_to': null,
        'calculateHours': true,
        'hoursCalculating': false,
        'isNotValid': false
      }
    },
    getContractorsSelectOptions () {
      return this._getPartnerRelatedUsers(this.currentPartner ? this.currentPartner.id : null)
    },
    isPartnerChoice () {
      return this.currentChoice === 'Partner'
    }
  },
  methods: {
    payoutItemChanged (index, attrName, attrValue) {
      const previousItem = cloneDeep(this.items[index])
      if (attrName === 'date_from' || attrName === 'date_to') {
        attrValue = this.convertDataToString(attrValue)
      }

      this.items[index][attrName] = attrValue
      const item = this.items[index]

      if (attrName === 'contractor') {
        item.project = null
      }
      if (attrName === 'calculateHours') {
        item.calculateHours = attrValue
      }
      item.amount = null

      if (['contractor', 'project', 'calculateHours', 'date_from', 'date_to'].includes(attrName)) {
        this.$emit('calculateHours', { item, previousItem })
      } else if (['hours', 'hour_cost'].includes(attrName)) {
        this.$emit('calculatePayout')
      }
    },
    removeItem (data) {
      this.items = this.items.filter((item, index) => index !== data.index)
      this.triggerInputEmit()
      this.$emit('calculatePayout')
    },
    formatNumber (number) {
      return number ? parseFloat(number).toString().replace() : ''
    }
  }
}
</script>

<style lang="scss">
  .finances-payouts-items-table{
    input.form-control {
      padding: 4px;
      text-align:center;
    }
    margin-bottom: 1rem;
    .table th,
    .table td {
      padding: 6px;
    }
    .item-type-column{
      min-width: 200px;
    }
    .select-column {
      width: 100px;
    }
    .hours-column{
      width: 65px;
      text-align: center;
    }
    .logged-hours-column{
      position: relative;
      width: 50px;
      padding: 0px;
      text-align: center;
    }
    .cost-column {
      width: 85px;
    }
    .hour-cost {
      position: relative;
    }
    .hour-cost input{
      padding-right: 15px;
      text-align: right;
    }
    .payment-type-label {
      color: grey;
      display: block;
      position: absolute;
      top: 5px;
      right: 2px;
      font-size: 11px;
    }
    .calculate-hours-column {
      min-width: 20px;
      max-width: 35px;
    }
    .commission-column {
      min-width: 50px;
      max-width: 70px;
    }
    .actions-column {
      min-width: 20px;
      max-width: 37px;
    }
    .date-column {
      min-width: 100px;
    }
    .date-column .vdp-datepicker .input-group .form-control {
      padding-right: 20px !important;
    }
    .hours-info{
      position:absolute;
      left: 17px;
      top: 3px;
    }
    .hours-info-label {
      font-size:0.7rem;
      margin-left:5px;
      margin-top:13px;
    }
    .hours-loader{
      width: 1.5rem;
      height: 1.5rem;
      margin-top: 6px;
    }
    .vdp-datepicker .input-group:after{
      right:1px;
    }
    .is-not-valid {
      border-left: 3px solid rgba(255, 0, 0, 0.5);
    }
  }
  .user-select {
    min-width: 167px;
  }
</style>
